/* TODO -- REWORK FOR REACT NATIVE */

import * as React from 'react'
import * as ReactDOM from 'react-dom'
import styled from 'styled-components'
import { View } from '@bestyled/primitives'
import { useMounting } from './mounting'

export interface ModalContextProps {
  ModalNode: any
  ModalBackground: any
}

export interface ModalComponentProps {
  afterOpen?: Function
  afterClose?: Function
  allowScroll?: boolean
  onEscapeKeydown?: Function
  onBackgroundClick?: Function
  isOpen?: boolean
  delayMount?: number
  delayUnmount?: number
}

const ModalContainerContext = React.createContext<ModalContextProps>(null!)

const useModal = () => React.useContext(ModalContainerContext)

const styledView = styled(View)`
  display: flex;
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 32;
  background-color: rgba(0, 0, 0, 0.5);
  align-items: center;
  justify-content: center;
`

export const ModalProvider: React.FC<{
  background?: React.Component
}> = (props) => {
  const [modalNode, setModalNode] = React.useState<any>(null)

  const modalBackground = React.useMemo(() => props.background || styledView, [
    props.background
  ])

  const didGetRef = React.useCallback((node) => setModalNode(node), [])

  const context = React.useMemo(
    () => ({ ModalNode: modalNode, ModalBackground: modalBackground }),
    [modalNode, modalBackground]
  )

  return (
    <ModalContainerContext.Provider value={context}>
      {props.children}
      <View ref={didGetRef} />
    </ModalContainerContext.Provider>
  )
}

export const Modal: React.FC<ModalComponentProps> = (props) => {
  const [stateOpen, setStateOpen] = React.useState(false)
  const node = React.useRef(null)
  const prevBodyOverflow = React.useRef<string | null>(null)

  React.useEffect(() => {
    if (!props.isOpen) {
      if (stateOpen) {
        cleanUp()
        if (props.afterClose) {
          props.afterClose()
        }
      }
    } else if (!stateOpen) {
      document.addEventListener('keydown', onKeydown)

      if (!props.allowScroll) {
        prevBodyOverflow.current = document.body.style.overflow
        document.body.style.overflow = 'hidden'
      }

      if (props.afterOpen) {
        props.afterOpen()
      }
    }
  }, [props.isOpen])

  const handleChange = (event, newState) => {
    if (props[event]) {
      try {
        props[event]().then(() => setStateOpen(newState))
      } catch (e) {
        setStateOpen(newState)
      }
    } else {
      setStateOpen(newState)
    }
  }

  React.useEffect(() => {
    return () => {
      if (props.isOpen) {
        cleanUp()
      }
    }
  })

  const onKeydown = React.useCallback(
    (e) => {
      if (e.key === 'Escape') {
        if (props.onEscapeKeydown) {
          props.onEscapeKeydown(e)
        }
      }
    },
    [props.onEscapeKeydown]
  )

  const cleanUp = React.useCallback(() => {
    document.removeEventListener('keydown', onKeydown)

    if (!props.allowScroll) {
      document.body.style.overflow = prevBodyOverflow.current || ''
    }
  }, [props.allowScroll])

  const onBackgroundClick = React.useCallback(
    (e) => {
      if (node.current === e.target) {
        if (props.onBackgroundClick) {
          props.onBackgroundClick(e)
        }
      }
    },
    [props.onBackgroundClick]
  )

  React.useEffect(() => {
    if (props.isOpen) {
      handleChange('beforeOpen', true)
    } else {
      handleChange('beforeClose', false)
    }
  }, [props.isOpen])

  const { ModalNode, ModalBackground } = useModal()

  const [mounting, unmounting] = useMounting({
    mounted: stateOpen,
    delayMount: props.delayMount,
    delayUnmount: props.delayUnmount
  })

  if (
    ModalNode &&
    ModalBackground &&
    ((stateOpen && !mounting) || unmounting)
  ) {
    return ReactDOM.createPortal(
      <ModalBackground onClick={onBackgroundClick} ref={node}>
        {props.children}
      </ModalBackground>,
      ModalNode
    )
  }

  return null
}
